import { create } from 'zustand';
import { api } from '../services/api';

export interface Reminder {
  id: string;
  family_id: string;
  user_id: string;
  title: string;
  body: string | null;
  icon: string;
  colour: string;
  remind_at: string;
  repeat_rule: string | null;
  repeat_day: string | null;
  repeat_start_date: string | null;
  enabled: boolean;
  completed_at: string | null;
  assigned_to?: string | null;
  assigned_to_name?: string | null;
  created_by: string;
  creator_name: string;
  created_at: string;
  updated_at: string;
  next_occurrence: string | null;
}

interface RemindersState {
  reminders: Reminder[];
  upcoming: Reminder[];
  isLoading: boolean;
  error: string | null;
  fetchReminders: () => Promise<void>;
  fetchUpcoming: () => Promise<void>;
  createReminder: (data: Partial<Reminder>) => Promise<Reminder>;
  updateReminder: (id: string, data: Partial<Reminder>) => Promise<void>;
  deleteReminder: (id: string) => Promise<void>;
  handleWsMessage: (message: any) => void;
}

export const useRemindersStore = create<RemindersState>((set, get) => ({
  reminders: [],
  upcoming: [],
  isLoading: false,
  error: null,

  fetchReminders: async () => {
    set({ isLoading: true, error: null });
    try {
      const res = await api.get('/reminders');
      set({ reminders: res.data.reminders || [], isLoading: false });
    } catch (err: any) {
      set({ error: err.message, isLoading: false });
    }
  },

  fetchUpcoming: async () => {
    try {
      const res = await api.get('/reminders/upcoming');
      set({ upcoming: res.data.reminders || [] });
    } catch { /* non-critical */ }
  },

  createReminder: async (data) => {
    const res = await api.post('/reminders', data);
    const reminder = res.data.reminder;
    set(state => ({ reminders: [reminder, ...state.reminders] }));
    return reminder;
  },

  updateReminder: async (id, data) => {
    const res = await api.put(`/reminders/${id}`, data);
    const updated = res.data.reminder;
    set(state => ({
      reminders: state.reminders.map(r => r.id === id ? updated : r),
    }));
  },

  deleteReminder: async (id) => {
    await api.delete(`/reminders/${id}`);
    set(state => ({
      reminders: state.reminders.filter(r => r.id !== id),
    }));
  },

  handleWsMessage: (message) => {
    const { type, payload } = message;
    if (type === 'REMINDER_CREATED') {
      set(state => {
        if (state.reminders.some(r => r.id === payload.id)) return state;
        return { reminders: [payload, ...state.reminders] };
      });
    } else if (type === 'REMINDER_UPDATED') {
      set(state => ({
        reminders: state.reminders.map(r => r.id === payload.id ? payload : r),
      }));
    } else if (type === 'REMINDER_DELETED') {
      set(state => ({
        reminders: state.reminders.filter(r => r.id !== payload.reminderId),
      }));
    }
  },
}));
